import { useCallback, useEffect, useRef, useState } from 'react'
import { supabase } from '../lib/supabase'
import { defaultContent } from '../lib/defaultContent'

export interface SiteContent {
  key: string
  title: string
  body_md: string
  updated_at: string | null
  /** False while nobody has edited it and the page is showing the built-in text */
  isDefault: boolean
}

/**
 * Text an officer can edit without a deploy.
 *
 * Falls back to the default written in src/lib/defaultContent.ts, so a page
 * is never blank and an officer opening the editor starts from real text.
 */
export function useSiteContent(key: string) {
  const [content, setContent] = useState<SiteContent | null>(null)
  const [loading, setLoading] = useState(true)
  const mountedRef = useRef(true)

  const fetchContent = useCallback(async () => {
    const fallback = defaultContent(key)
    const { data } = await supabase
      .from('site_content')
      .select('key, title, body_md, updated_at')
      .eq('key', key)
      .maybeSingle()

    if (!mountedRef.current) return
    setContent(
      data
        ? { ...data, title: data.title ?? fallback.title, isDefault: false }
        : { key, title: fallback.title, body_md: fallback.body, updated_at: null, isDefault: true },
    )
    setLoading(false)
  }, [key])

  useEffect(() => {
    mountedRef.current = true
    setLoading(true)
    fetchContent()
    return () => { mountedRef.current = false }
  }, [fetchContent])

  const save = useCallback(async (
    body_md: string,
    title?: string,
  ): Promise<{ error: string | null }> => {
    const { data: { user } } = await supabase.auth.getUser()
    const { data, error } = await supabase
      .from('site_content')
      .upsert({
        key,
        title: title ?? content?.title ?? null,
        body_md,
        updated_by: user?.id ?? null,
        updated_at: new Date().toISOString(),
      }, { onConflict: 'key' })
      .select('key, title, body_md, updated_at')
      .single()

    if (error) return { error: error.message }
    if (mountedRef.current && data) {
      setContent({ ...data, title: data.title ?? '', isDefault: false })
    }
    return { error: null }
  }, [key, content?.title])

  /** Put a page back to the text shipped in the code. */
  const reset = useCallback(async (): Promise<{ error: string | null }> => {
    const { error } = await supabase.from('site_content').delete().eq('key', key)
    if (error) return { error: error.message }
    await fetchContent()
    return { error: null }
  }, [key, fetchContent])

  return { content, loading, save, reset, refetch: fetchContent }
}

/**
 * Upload an image for use inside editable content.
 *
 * Officers paste screenshots into rules pages; a markdown editor is no use
 * without somewhere for those to live. Returns a public URL to drop into the
 * body as an image.
 */
export async function uploadContentImage(file: File): Promise<{ url: string | null; error: string | null }> {
  const extension = file.name.split('.').pop()?.toLowerCase() || 'png'
  const path = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}.${extension}`

  const { error } = await supabase.storage.from('site-content').upload(path, file, {
    cacheControl: '31536000',
    upsert: false,
  })
  if (error) return { url: null, error: error.message }

  const { data } = supabase.storage.from('site-content').getPublicUrl(path)
  return { url: data.publicUrl, error: null }
}
